import { calculateJustifiedLayout } from 'src/utils/justifiedAssetLayout';
import { useEffect, useRef, useState } from 'react';
import { AssetPublishModel } from 'src/models/assetModel';
import { AssetType, AssetUsage } from 'src/constant/assetConstants';
import { HeartOutlined, PlayCircleOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import HoverVideoPlayer from 'react-hover-video-player';
import { useNavigate } from 'react-router-dom';
import { useSessionContext } from 'src/context/session-context';
import { message } from 'antd';
import { RouteNames } from 'src/routes/routeName';
import { ArtistService } from 'src/services/artistService';
import { HttpStatus } from 'src/constant/responseStatus';
import MyFavoriteModal from '../modal/myFavoriteModal';
import AddToCartModal from '../modal/addToCartModal';
import TranslationKey from 'src/i18n/translation';
const aiGeneratedIcon = require("../../assets/images/gallery-item/ai_usage_type.png");

interface Props {
  assets: AssetPublishModel[];
  publishImages?: AssetPublishModel[];
  rowHeight?: number;
  margin?: number;
  leftOffset?: number;
}

const JustifiedGallery: React.FC<Props> = ({
  assets,
  rowHeight = 250,
  margin = 15,
  leftOffset = 20,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const navigate = useNavigate();
  const { userProfile } = useSessionContext();
  const [isOpenFavorite, setIsOpenFavorite] = useState(false);
  const [isOpenAddToCart, setIsOpenAddToCart] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);

  const onClickFavorite = (assetId: string) => {
    if (userProfile?.userId) {
      setSelectedAssetId(assetId);
      setIsOpenFavorite(true);
    } else {
      message.error(TranslationKey('loginToUseThisFeature'));
    }
  };

  const addToCard = (assetId: string) => {
    setSelectedAssetId(assetId);
    setIsOpenAddToCart(true);
  };

  const handleCloseFav = () => {
    setIsOpenFavorite(false);
  };

  const onClickToAssetDetail = (assetId: string) => {
    navigate(RouteNames.assetDetail.replace(':id', assetId));
  }

  const onClickToArtist = async (assetId: string) => {
    const { data, status } = await new ArtistService().GetIdByCode(assetId);

    if (status !== HttpStatus.OK) {
      return;
    }

    navigate(RouteNames.artistProfile.replace(':id', data));
  };

  const [layout, setLayout] = useState(() => calculateJustifiedLayout(assets, {
    containerWidth: 1200,
    targetRowHeight: rowHeight,
    margin,
    leftOffset: leftOffset,
  }));

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const fullWidth = containerRef.current.offsetWidth;
        const padding = leftOffset * 2
        setContainerWidth(fullWidth - padding);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    if (containerWidth > 0) {
      const newLayout =
        calculateJustifiedLayout(assets, {
          containerWidth,
          targetRowHeight: rowHeight,
          margin,
          leftOffset: leftOffset,
        });

      setLayout(newLayout);
    }
  }, [containerWidth, assets, rowHeight, margin]);

  const containerHeight =
    layout.reduce((max, box) =>
      Math.max(max, box.top + box.height), 0);

  return (
    <>
      <div className='justified-gallery'
        ref={containerRef}
        style={{
          height: containerHeight
        }}
      >
        {
          layout.map((box, boxIndex) => (
            <div className='justified-gallery-item'
              key={box.asset.id}
              style={{
                position: 'absolute',
                left: box.left,
                top: box.top,
                width: box.width,
                height: box.height,
                overflow: 'hidden',
                borderRadius: 8,
              }}
            >
              {
                box.asset.assetType.toUpperCase() === AssetType.IMAGE.toUpperCase() &&
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                  }}
                  onClick={() => onClickToAssetDetail(box.asset.id)}>
                  <img
                    key={'gallery-justified-item-image-' + boxIndex}
                    src={box.asset.thumbnailImageUrl}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                </div>
              }
              {
                box.asset.assetType.toUpperCase() === AssetType.VIDEO.toUpperCase() &&
                <HoverVideoPlayer
                  onClick={() => onClickToAssetDetail(box.asset.id)}
                  key={'gallery-horizonal-item-video-' + boxIndex}
                  videoSrc={box.asset.thumbnailVideoUrl!}
                  restartOnPaused
                  overlayTransitionDuration={500}
                  style={{
                    position: 'relative',
                    height: '100%',
                    borderRadius: 5,
                  }}
                  videoStyle={{
                    position: 'relative',
                    height: '100%',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    borderRadius: 8,
                  }}
                />
              }
              {
                box.asset.assetType.toUpperCase() === AssetType.VIDEO.toUpperCase() &&
                <PlayCircleOutlined
                  style={{
                    fontSize: 20,
                    color: 'white',
                    position: 'absolute',
                    top: 10,
                    left: 10,
                  }}
                />
              }
              {
                box.asset.usage?.toUpperCase() === AssetUsage.AiGenerated.toUpperCase() &&
                <img src={aiGeneratedIcon} style={{
                  width: 12,
                  color: 'white',
                  position: 'absolute',
                  top: box.asset.assetType.toUpperCase() === AssetType.VIDEO.toUpperCase() ? 40 : 10,
                  left: 10,
                }} />
              }
              {
                <HeartOutlined
                  onClick={() => onClickFavorite(box.asset.id)}
                  className='hover-icon'
                  style={{
                    fontSize: 20,
                    color: 'white',
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    zIndex: 10,
                  }}
                />
              }
              {
                <ShoppingCartOutlined
                  onClick={() => addToCard(box.asset.id)}
                  className='hover-icon'
                  style={{
                    fontSize: 20,
                    color: 'white',
                    position: 'absolute',
                    top: 37,
                    right: 10,
                    zIndex: 10,
                  }}
                />
              }
              <div className='hover-label'
                onClick={() => onClickToArtist(box.asset.id)}
                style={{
                  fontSize: 15,
                  color: 'white',
                  position: 'absolute',
                  bottom: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10,
                }}
              >
                {
                  box.asset &&
                    (
                      box.asset.artistNameEn == null ||
                      box.asset.artistNameEn === undefined ||
                      box.asset.artistNameEn === ''
                    )
                    ? box.asset.artistFirstname
                    : box.asset.artistNameEn
                }
              </div>
            </div>
          ))}
      </div>

      <MyFavoriteModal
        isFavoriteModalOpen={isOpenFavorite}
        assetId={selectedAssetId!}
        onCloseFavoriteModal={handleCloseFav}
      />

      <AddToCartModal
        isModalOpen={isOpenAddToCart}
        onCloseModal={() => setIsOpenAddToCart(false)}
        assetId={selectedAssetId!} />
    </>
  );
};

export default JustifiedGallery;
